import React, { useEffect, useState } from "react";
import { Search, Loader2, Mail, Phone, ExternalLink, Clock, Banknote, MapPin } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";

export default function MunicipalityFinder() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/municipalities", { params: { q: query || undefined } });
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const open = async (slug) => {
    try {
      const { data } = await api.get(`/municipalities/${slug}`);
      setSelected(data);
    } catch {}
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in-up">
      <Seo
        title="Hitta din kommun"
        description="Sök din svenska kommun och hitta kontaktinformation, e-post, telefon och avgifter för bygglovsenheten. Bygglovskompassen listar alla 290 kommuner i Sverige."
        path="/municipality"
        keywords="kommun bygglov, kontakt bygglovsenheten, kommun e-post bygglov"
      />
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-blue-700 text-white flex items-center justify-center rounded-sm">
          <MapPin className="w-6 h-6 stroke-[1.5]" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Hitta kommunen</div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>Sök din kommun</h1>
        </div>
      </div>
      <p className="mt-3 text-zinc-600">Hitta lokala bygglovsregler, handläggningstider, avgifter och kontaktvägar.</p>

      <div className="mt-8 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
        <input
          data-testid="muni-search"
          value={q}
          onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
          placeholder="Sök kommun..."
          className="w-full pl-10 pr-3 py-3 border border-zinc-300 rounded-sm focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none text-base bg-white"
        />
      </div>

      <div className="mt-6 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-1 bg-white border border-zinc-200 rounded-sm max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-10 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500">Inga träffar</div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {items.map((m) => (
                <li key={m.slug}>
                  <button
                    data-testid={`muni-${m.slug}`}
                    onClick={() => open(m.slug)}
                    className={`w-full text-left px-4 py-3 hover:bg-zinc-50 transition ${
                      selected?.slug === m.slug ? "bg-blue-50/60" : ""
                    }`}
                  >
                    <div className="font-medium text-sm text-zinc-900">{m.name}</div>
                    <div className="text-xs text-zinc-500">{m.region} · ~{m.avg_weeks} v handl.tid</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="md:col-span-2">
          {!selected ? (
            <div className="bg-zinc-50 border border-zinc-200 rounded-sm py-16 text-center text-sm text-zinc-500">
              Välj en kommun till vänster för att se detaljer
            </div>
          ) : (
            <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="muni-detail">
              <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>{selected.name}</h2>
              <div className="text-sm text-zinc-500">{selected.region}</div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4">
                  <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> Handläggningstid
                  </div>
                  <div className="mt-2 text-xl font-semibold" style={{ fontFamily: "Outfit" }}>~{selected.avg_weeks} veckor</div>
                </div>
                <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4">
                  <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500 flex items-center gap-1.5">
                    <Banknote className="w-3.5 h-3.5" /> Avgift (vägl.)
                  </div>
                  <div className="mt-2 text-sm font-medium">{selected.fee}</div>
                </div>
              </div>

              {selected.notes && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-sm text-sm text-blue-900">
                  {selected.notes}
                </div>
              )}

              <div className="mt-6 space-y-2">
                <a href={`mailto:${selected.email}`} className="flex items-center gap-2 text-sm text-blue-700 hover:underline">
                  <Mail className="w-3.5 h-3.5" /> {selected.email}
                </a>
                <a href={`tel:${selected.phone}`} className="flex items-center gap-2 text-sm text-blue-700 hover:underline">
                  <Phone className="w-3.5 h-3.5" /> {selected.phone}
                </a>
                <a href={selected.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-blue-700 hover:underline">
                  <ExternalLink className="w-3.5 h-3.5" /> {selected.url}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
